/* eslint-disable camelcase */
import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TablePagination,
  CircularProgress,
  Paper,
  Typography,
  Box,
  IconButton,
  Card,
  Chip,
  alpha,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Scrollbar from '../scrollbar';
import { UserListHead } from '../../sections/@dashboard/user';
import ClientActionsMenu from './ClientActionsMenu';
import Iconify from '../iconify';
import { LightTooltip } from '../custom-components/custom-tooltip';

const TABLE_HEAD = [
  { id: 'Practice Name', label: 'Practice Name', alignRight: false },
  { id: 'Username', label: 'Username', alignRight: false },
  { id: 'Email', label: 'Email', alignRight: false },
  { id: 'Phone', label: 'Phone', alignRight: false },
  { id: 'Status', label: 'Status', alignRight: false },
  { id: 'Assigned To', label: 'Assigned To', alignRight: false },
  { id: 'Steps', label: 'Steps', alignRight: false },
  { id: 'Actions', label: '', alignRight: false },
];

export default function ClientsTable({
  clients,
  pageLoader,
  pagination,
  rowsPerPage,
  order,
  orderBy,
  selected,
  filterName,
  isNotFound,
  onRequestSort,
  onSelectAllClick,
  onPageChange,
  onRowsPerPageChange,
  onAssignmentDialogOpen,
  onUnassignClient,
  onClientActions,
}) {
  const [anchorEl, setAnchorEl] = useState({});

  const handleActionClick = (event, menuId) => {
    setAnchorEl({ [menuId]: event.currentTarget });
  };

  const handleClose = () => {
    setAnchorEl({});
  };

  const emptyRows = 0; // No empty rows needed with server-side pagination

  return (
    <Card>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <UserListHead
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              rowCount={clients.length}
              numSelected={selected.length}
              onRequestSort={onRequestSort}
              onSelectAllClick={onSelectAllClick}
            />
            {pageLoader ? (
              <TableRow>
                <TableCell align="center" colSpan={8} sx={{ py: 8 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    Loading clients...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              clients.length === 0 && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={8} sx={{ py: 10 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Iconify
                          icon="eva:people-outline"
                          sx={{ mb: 2, width: 48, height: 48, color: 'text.disabled' }}
                        />
                        <Typography variant="h6" gutterBottom>
                          No Clients Found
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          There are currently no clients in the directory.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )
            )}

            <TableBody>
              {clients?.map((row, index) => {
                const { id, username, email, practice_name, phone, status, steps, w9_copy_exists, assigned_employee } =
                  row;
                const isSelected = selected.indexOf(email) !== -1;

                return (
                  <TableRow
                    hover
                    key={id}
                    tabIndex={-1}
                    role="checkbox"
                    selected={isSelected}
                    sx={{
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <TableCell align="left">
                      <Typography variant="subtitle2" noWrap>
                        {practice_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">{username}</TableCell>
                    <TableCell align="left">{email}</TableCell>
                    <TableCell align="left">{phone}</TableCell>
                    <TableCell align="left">
                      <Chip
                        label={status}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          fontSize: '0.65rem',
                          backgroundColor: (theme) => {
                            switch (status) {
                              case 'Locked':
                                return alpha(theme.palette.warning.main, 0.12);
                              case 'Active':
                                return alpha(theme.palette.success.main, 0.12);
                              default:
                                return theme.palette.action.selected;
                            }
                          },
                          color: (theme) => {
                            switch (status) {
                              case 'Locked':
                                return theme.palette.warning.main;
                              case 'Active':
                                return theme.palette.success.main;
                              default:
                                return theme.palette.text.primary;
                            }
                          },
                          border: (theme) => {
                            let color;
                            switch (status) {
                              case 'Locked':
                                color = theme.palette.warning.main;
                                break;
                              case 'Active':
                                color = theme.palette.success.main;
                                break;
                              default:
                                color = theme.palette.divider;
                            }
                            return `1px solid ${alpha(color, 0.24)}`;
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="left">
                      {assigned_employee ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{assigned_employee.full_name}</Typography>
                          <LightTooltip title="Edit Assignment">
                            <IconButton
                              size="small"
                              onClick={() => onAssignmentDialogOpen(row)}
                              sx={{ color: 'primary.main' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </LightTooltip>
                          <LightTooltip title="Unassign Employee">
                            <IconButton size="small" onClick={() => onUnassignClient(row)} sx={{ color: 'error.main' }}>
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </LightTooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            Unassigned
                          </Typography>
                          <LightTooltip title="Assign Employee">
                            <IconButton
                              size="small"
                              onClick={() => onAssignmentDialogOpen(row)}
                              sx={{ color: 'success.main' }}
                            >
                              <PersonAddIcon fontSize="small" />
                            </IconButton>
                          </LightTooltip>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="left">
                      {steps !== undefined && steps.length === 0 && '-'}
                      {steps !== undefined && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {steps.map((step) => (
                            <Box key={step.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LightTooltip title={step.status}>
                                <Box
                                  component="img"
                                  src={step.status === 'Done' ? '/assets/iconDone.png' : '/assets/iconProcess.png'}
                                  alt={step.status}
                                  sx={{ height: 20, width: 20 }}
                                />
                              </LightTooltip>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {step.name}
                                {step.remarks ? `: ${step.remarks}` : ''}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="left">
                      <LightTooltip title="Client Actions" disableHoverListener={Boolean(anchorEl[index])}>
                        <IconButton
                          aria-controls="client-actions-menu"
                          aria-haspopup="true"
                          onClick={(e) => {
                            handleActionClick(e, index);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </LightTooltip>
                      <ClientActionsMenu
                        anchorEl={anchorEl[index]}
                        open={Boolean(anchorEl[index])}
                        onClose={handleClose}
                        onClientActions={onClientActions}
                        clientId={id}
                        status={status}
                        row={row}
                        w9_copy_exists={w9_copy_exists}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={7} />
                </TableRow>
              )}
            </TableBody>

            {isNotFound && (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                    <Paper
                      sx={{
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="h6" paragraph>
                        Not found
                      </Typography>

                      <Typography variant="body2">
                        No results found for &nbsp;
                        <strong>&quot;{filterName}&quot;</strong>.
                        <br /> Try checking for typos or using complete words.
                      </Typography>
                    </Paper>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Scrollbar>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={pagination?.total_count}
        rowsPerPage={rowsPerPage}
        page={pagination?.current_page - 1} // Material-UI uses 0-based indexing
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Card>
  );
}
